import { useCallback, useEffect, useMemo, useReducer } from 'react'

import { resolveUsers } from '../services/directoryServices'

/**
 * IDs de usuário circulam em duas grafias: os herdados do sistema antigo são
 * maiúsculos, os criados pelo SSO atual são minúsculos. Normalizar antes de
 * indexar evita "não encontrado" por diferença de caixa.
 */
const chave = id => String(id ?? '').trim().toUpperCase()

/**
 * Cache do módulo, não do componente.
 *
 * As telas montam e desmontam o tempo todo — troca de aba nos relatórios, abrir
 * e fechar uma ordem, refiltrar a listagem — e um cache por instância refaria o
 * mesmo lote a cada montagem, piscando GUID antes do nome. Aqui o nome já
 * resolvido vale para a página inteira. Vive enquanto a aba viver: nome de
 * usuário muda raramente, e um F5 renova.
 *
 * Valor `null` significa "o SSO não conhece este ID" — distinto de ausente, que
 * significa "ainda não perguntei". Sem essa diferença, um usuário removido
 * seria rebuscado para sempre.
 */
const cacheDeNomes = new Map()

/** IDs com busca em andamento, para duas telas não pedirem o mesmo lote. */
const emVoo = new Set()

/** Componentes vivos a avisar quando o cache muda. */
const inscritos = new Set()

const avisarTodos = () => inscritos.forEach(avisar => avisar())

/**
 * Resolve IDs e popula o cache do módulo SEM um componente — para a exportação,
 * que precisa do nome de TODAS as linhas do filtro (não só da página na tela).
 *
 * Depois de aguardar esta função, `nomeDe` já devolve os nomes: ele lê o mesmo
 * `cacheDeNomes` vivo, então nem precisa de novo render. Falha do SSO não grava
 * nada — o export cai para o ID, como a tela.
 */
export async function primeUserNames(ids) {
  const faltantes = [
    ...new Set((ids ?? []).map(id => chave(id)).filter(Boolean)),
  ].filter(id => !cacheDeNomes.has(id))
  if (faltantes.length === 0) return

  // Fatia em blocos: o endpoint do SSO aceita no máximo um lote por chamada.
  const BLOCO = 200
  for (let i = 0; i < faltantes.length; i += BLOCO) {
    const lote = faltantes.slice(i, i + BLOCO)
    try {
      const usuarios = await resolveUsers(lote)
      lote.forEach(id => cacheDeNomes.set(id, null))
      for (const usuario of usuarios ?? []) {
        if (usuario?.id) cacheDeNomes.set(chave(usuario.id), usuario.fullName || null)
      }
    } catch {
      // Sem o SSO, o export cai para o ID — melhor que travar a exportação.
    }
  }
  avisarTodos()
}

/**
 * Resolve IDs de usuário em nomes, via SSO.
 *
 * Nome é rótulo de tela, não dado da operação: se o SSO falhar ou o usuário não
 * existir mais, `nomeDe` devolve o próprio ID e a tela continua utilizável. Por
 * isso nada aqui lança nem bloqueia a renderização.
 *
 * @param {string[]} ids IDs a resolver — pode repetir e conter vazios.
 * @returns {{ nomeDe: (id: string) => string, carregando: boolean }}
 */
const useUserNames = ids => {
  const [versao, mudou] = useReducer(n => n + 1, 0)

  useEffect(() => {
    inscritos.add(mudou)
    return () => inscritos.delete(mudou)
  }, [])

  const idsUnicos = useMemo(() => {
    const conjunto = new Set()
    for (const id of ids ?? []) {
      const normalizado = chave(id)
      if (normalizado) conjunto.add(normalizado)
    }
    return [...conjunto].sort()
  }, [ids])

  // A lista remonta a cada filtro digitado; comparar pela assinatura em vez do
  // array evita refazer a busca a cada tecla.
  const assinatura = idsUnicos.join(',')

  const faltantes = idsUnicos.filter(id => !cacheDeNomes.has(id) && !emVoo.has(id))
  const carregando = idsUnicos.some(id => emVoo.has(id)) || faltantes.length > 0

  useEffect(() => {
    const pendentes = idsUnicos.filter(id => !cacheDeNomes.has(id) && !emVoo.has(id))
    if (pendentes.length === 0) return

    pendentes.forEach(id => emVoo.add(id))

    resolveUsers(pendentes)
      .then(usuarios => {
        // Marca todo o lote como perguntado antes de gravar o que voltou: quem
        // não veio é usuário removido, e sem isso seria rebuscado sem parar.
        pendentes.forEach(id => cacheDeNomes.set(id, null))
        for (const usuario of usuarios ?? []) {
          if (usuario?.id) cacheDeNomes.set(chave(usuario.id), usuario.fullName || null)
        }
      })
      .catch(() => {
        // Falha do SSO não grava nada: a tela cai para o ID e uma próxima
        // montagem tenta de novo, em vez de fixar a lacuna até o F5.
      })
      .finally(() => {
        pendentes.forEach(id => emVoo.delete(id))
        avisarTodos()
      })
    // `versao` entra para reavaliar quando outra tela resolveu parte do lote.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assinatura, versao])

  const nomeDe = useCallback(
    id => {
      const normalizado = chave(id)
      if (!normalizado) return '—'
      return cacheDeNomes.get(normalizado) || id
    },
    // Não depende de `id`: o que muda a resposta é o cache, e `versao` é o
    // sinal de que ele mudou. Sem isto, as colunas memoizadas ficariam presas
    // no GUID mesmo depois do nome chegar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [versao]
  )

  return { nomeDe, carregando }
}

export default useUserNames
